import React, { useRef, useEffect, useState, useCallback } from 'react';
import Hls from 'hls.js';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Maximize2, Minimize2, Check } from 'lucide-react';
import { motion } from 'framer-motion';

interface CustomHLSPlayerProps {
    src: string;
    title?: string;
    onNext?: () => void;
    onPrevious?: () => void;
    hasNext?: boolean;
    hasPrevious?: boolean;
    className?: string;
    onVideoReady?: (playFn: () => Promise<void>) => void;
    hideNextStepModal?: boolean;
    hideControlsOnAutoPlay?: boolean;
    disableFullscreen?: boolean;
    hideBottomControls?: boolean;
    showTimeInTopLeft?: boolean;
}

const CustomHLSPlayer: React.FC<CustomHLSPlayerProps> = ({
    src,
    title,
    onNext,
    onPrevious,
    hasNext = false,
    hasPrevious = false,
    className = '',
    onVideoReady,
    hideNextStepModal = false,
    hideControlsOnAutoPlay = false,
    disableFullscreen = false,
    hideBottomControls = false,
    showTimeInTopLeft = false
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [showNextStepPopup, setShowNextStepPopup] = useState(false);
    const [pendingPlayFn, setPendingPlayFn] = useState<(() => Promise<void>) | null>(null);

    const hideControlsTimeoutRef = useRef<NodeJS.Timeout>();

    const showControlsTemporarily = useCallback(() => {
        setShowControls(true);
        if (hideControlsTimeoutRef.current) {
            clearTimeout(hideControlsTimeoutRef.current);
        }
        hideControlsTimeoutRef.current = setTimeout(() => {
            if (isPlaying) {
                setShowControls(false);
            }
        }, 3000);
    }, [isPlaying]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        // Reset states when source changes to prevent sync issues
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setIsLoading(true);

        // Pause any existing video
        video.pause();

        if (Hls.isSupported()) {
            const hls = new Hls({
                enableWorker: false,
            });
            hlsRef.current = hls;
            hls.loadSource(src);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setIsLoading(false);
                // Provide play function to parent for user-initiated playback
                if (onVideoReady && video) {
                    onVideoReady(() => video.play());
                }
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS Error:', data);
                if (data.fatal) {
                    setIsLoading(false);
                }
            });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = src;
            setIsLoading(false);
            // Provide play function to parent for user-initiated playback
            if (onVideoReady) {
                onVideoReady(() => video.play());
            }
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
            }
        };
    }, [src]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => setCurrentTime(video.currentTime);
        const handleLoadedMetadata = () => setDuration(video.duration);
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleVolumeChange = () => {
            setVolume(video.volume);
            setIsMuted(video.muted);
        };

        const handleVideoEnded = () => {
            // Ensure timer shows correct final time when video ends
            if (video.duration) {
                setCurrentTime(video.duration);
            }

            if (hasNext) {
                setShowNextStepPopup(true);
            }
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('volumechange', handleVolumeChange);
        video.addEventListener('ended', handleVideoEnded);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('volumechange', handleVolumeChange);
            video.removeEventListener('ended', handleVideoEnded);
        };
    }, []);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Hide modal when parent requests it
    useEffect(() => {
        if (hideNextStepModal) {
            setShowNextStepPopup(false);
        }
    }, [hideNextStepModal]);

    // Hide controls when auto-playing
    useEffect(() => {
        if (hideControlsOnAutoPlay) {
            setShowControls(false);
        }
    }, [hideControlsOnAutoPlay]);

    const togglePlayPause = () => {
        const video = videoRef.current;
        if (!video) return;

        if (isPlaying) {
            video.pause();
        } else {
            video.play();
        }
    };

    const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        const video = videoRef.current;
        if (!video || !duration) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        const newTime = percent * duration;
        video.currentTime = newTime;
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const video = videoRef.current;
        if (!video) return;

        const newVolume = parseFloat(e.target.value);
        video.volume = newVolume;
        setVolume(newVolume);
        setIsMuted(newVolume === 0);
    };

    const toggleMute = () => {
        const video = videoRef.current;
        if (!video) return;

        video.muted = !isMuted;
    };

    const toggleFullscreen = async () => {
        const container = containerRef.current;
        if (!container) return;

        try {
            if (!document.fullscreenElement) {
                await container.requestFullscreen();
            } else {
                await document.exitFullscreen();
            }
        } catch (error) {
            console.error('Fullscreen error:', error);
        }
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const formatDuration = (time: number) => {
        // Reduce duration by 1 second for display to match expected timing
        const adjustedTime = time > 0 ? Math.max(0, time - 1) : 0;
        const minutes = Math.floor(adjustedTime / 60);
        const seconds = Math.floor(adjustedTime % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleNextStepFromPopup = () => {
        setShowNextStepPopup(false);
        if (onNext) {
            onNext();
            // After onNext changes the video source, we'll get a new play function
            // and can immediately call it since this is a user-initiated action
        }
    };

    const handleStayOnCurrentStep = () => {
        setShowNextStepPopup(false);
        // Reset video to beginning
        const video = videoRef.current;
        if (video) {
            video.currentTime = 0;
        }
    };

    const progressPercent = duration ? (currentTime / duration) * 100 : 0;

    return (
        <div
            ref={containerRef}
            className={`relative bg-black overflow-hidden group ${isFullscreen ? 'rounded-none' : 'rounded-hero'
                } ${className}`}
            onMouseMove={showControlsTemporarily}
            onMouseLeave={() => isPlaying && setShowControls(false)}
        >
            <video
                ref={videoRef}
                className={`w-full h-full ${isFullscreen ? 'object-contain' : 'object-cover'}`}
                onClick={togglePlayPause}
                onDoubleClick={disableFullscreen ? undefined : toggleFullscreen}
                playsInline
                webkit-playsinline="true"
            />

            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="animate-spin w-12 h-12 border-4 border-orange border-t-transparent rounded-full"></div>
                </div>
            )}

            {/* Controls Overlay */}
            <div
                className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'
                    }`}
            >
                {/* Title or Time */}
                <div className="absolute top-0 left-0 right-0 p-4 md:p-6">
                    {showTimeInTopLeft ? (
                        <div className="text-white font-body text-sm bg-black bg-opacity-60 rounded px-2 py-1 inline-block">
                            {formatTime(currentTime)} / {formatDuration(duration)}
                        </div>
                    ) : (
                        title && (
                            <h2 className="text-white font-headline text-xl md:text-2xl font-medium">
                                {title}
                            </h2>
                        )
                    )}
                </div>

                {/* Main Play Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <button
                        onClick={togglePlayPause}
                        className="w-16 h-16 md:w-20 md:h-20 bg-kibuBlue hover:bg-kibuBlue/90 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 shadow-lg"
                    >
                        {isPlaying ? (
                            <Pause className="w-8 h-8 md:w-10 md:h-10 text-white ml-0.5" />
                        ) : (
                            <Play className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" />
                        )}
                    </button>
                </div>

                {/* Bottom Controls */}
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                    {/* Progress Bar */}
                    <div
                        className="w-full h-2 bg-white/30 rounded-full cursor-pointer mb-4"
                        onClick={handleSeek}
                    >
                        <div
                            className="h-full bg-orange rounded-full transition-all duration-150"
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>

                    {/* Control Buttons */}
                    {!hideBottomControls && (
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                {/* Previous Step */}
                                <button
                                    onClick={onPrevious}
                                    disabled={!hasPrevious}
                                    className="text-white hover:text-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <SkipBack className="w-6 h-6" />
                                </button>

                                {/* Play/Pause */}
                                <button
                                    onClick={togglePlayPause}
                                    className="text-white hover:text-orange transition-colors"
                                >
                                    {isPlaying ? (
                                        <Pause className="w-6 h-6" />
                                    ) : (
                                        <Play className="w-6 h-6" />
                                    )}
                                </button>

                                {/* Next Step */}
                                <button
                                    onClick={onNext}
                                    disabled={!hasNext}
                                    className="text-white hover:text-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <SkipForward className="w-6 h-6" />
                                </button>

                                {/* Time */}
                                <div className="text-white font-body text-sm">
                                    {formatTime(currentTime)} / {formatDuration(duration)}
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                {/* Volume Control */}
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={toggleMute}
                                        className="text-white hover:text-orange transition-colors"
                                    >
                                        {isMuted ? (
                                            <VolumeX className="w-5 h-5" />
                                        ) : (
                                            <Volume2 className="w-5 h-5" />
                                        )}
                                    </button>
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        value={isMuted ? 0 : volume}
                                        onChange={handleVolumeChange}
                                        className="w-20 h-1 bg-white/30 rounded-full appearance-none cursor-pointer"
                                        style={{
                                            background: `linear-gradient(to right, #FF784D 0%, #FF784D ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.3) ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.3) 100%)`,
                                            WebkitAppearance: 'none',
                                            MozAppearance: 'none',
                                            appearance: 'none'
                                        }}
                                    />
                                </div>

                                {/* Fullscreen */}
                                {!disableFullscreen && (
                                    <button
                                        onClick={toggleFullscreen}
                                        className="text-white hover:text-orange transition-colors"
                                    >
                                        {isFullscreen ? (
                                            <Minimize2 className="w-5 h-5" />
                                        ) : (
                                            <Maximize2 className="w-5 h-5" />
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Next Step Popup */}
            {showNextStepPopup && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-50">
                    <motion.div
                        initial={{ scale: 0.88, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 26 }}
                        className="bg-white rounded-2xl p-6 md:p-8 max-w-sm mx-4 shadow-2xl w-full"
                    >
                        <div className="text-center">
                            <motion.div
                                initial={{ scale: 0, rotate: -90 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: 'spring', stiffness: 320, damping: 20, delay: 0.12 }}
                                className="w-16 h-16 bg-orange rounded-full flex items-center justify-center mx-auto mb-4 shadow-md"
                            >
                                <Check className="w-8 h-8 text-white" strokeWidth={2.5} />
                            </motion.div>
                            <h3 className="text-xl font-headline font-medium text-black mb-2">
                                Step Complete!
                            </h3>
                            <p className="text-gray-500 font-body mb-6">
                                Ready to move on?
                            </p>

                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={handleNextStepFromPopup}
                                    className="w-full bg-orange text-white py-3 px-6 rounded-full hover:bg-orange/90 transition-all hover:shadow-button-hover font-body font-medium"
                                >
                                    Continue to Next Step
                                </button>
                                <button
                                    onClick={handleStayOnCurrentStep}
                                    className="w-full border-2 border-grey text-gray-600 py-3 px-6 rounded-full hover:border-gray-400 transition-colors font-body"
                                >
                                    Replay This Step
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

        </div>
    );
};

export default CustomHLSPlayer;